/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import React, { useState } from "react";
import FormFader from "../Common/Fader/FormFader";
import Hidden from "@mui/material/Hidden";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import Paper from "@mui/material/Paper";
import { Box, Checkbox, FormControlLabel, Grid, MenuItem, Stack } from "@mui/material";
import { useForm } from "../../../provider/Form/FormProvider";
import Group from "../../Wizard/Group";
import CrossIcon from "../../../assets/icons/common/toggleable/IconClose";
import CheckIcon from "../../../assets/icons/common/toggleable/IconCheck";
import { isRequired } from "../../../services/validationRules";
import ValidatorSelectField from "../../FormFields/ValidatorSelectField";
import { formValue } from "../../../services/formServiceFunctions";
import PensionsDivorceModal from "../../FormInfoModal/PensionsDivorceModal";
import Typography from "@mui/material/Typography";
import { translate } from "../../../services/translations/translations";
import FormSubmitProps from "../../../types/FormSubmitProps";
import wizardStyle from "../wizardStyle";
import InfoIconButton from "../../Button/InfoIconButton";

type PensionsProps = {
  submitProps: FormSubmitProps;
};
export default function Pensions({ submitProps }: PensionsProps) {
  const [pensionsModalOpen, setPensionsModalOpen] = useState("");
  const { values, handleChange, handleSubmit } = useForm();
  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Hidden smUp>
            <Typography variant={"h4"} sx={wizardStyle.headline}>
              Finanzen
            </Typography>
          </Hidden>
          <Typography variant={"h4"} sx={wizardStyle.subheadline}>
            {translate("divorce.pensionsExcluded.label", values)}
          </Typography>
          <Group
            type="radio"
            name="pensionsExcluded"
            question={translate("divorce.pensionsExcluded.question", values)}
            modalContent={
              <>
                <p>
                  Bei einer Scheidung führt das Gericht immer den Versorgungsausgleich durch. Hierbei geht es um die Teilung der Rentenansprüche der Eheleute
                  aus der Ehezeit. Im Normalfall geht es um den Ausgleich von 2 Rentenansprüchen: je eine gesetzliche Rentenversicherung pro Ehepartner. Sollten
                  Sie jedoch zum Beispiel zusätzlich eine private Rentenversicherung (betriebliche Altersvorsorge oder Riesterrente) haben, geben Sie bei der
                  Anzahl Rentenversicherungen eine 2 an.
                </p>
                <p>Der Ausgleich ist gesetzlich vorgeschrieben, Sie können aber gemeinsam mit Ihrem Ehepartner per Notar auf den Ausgleich verzichten.</p>
              </>
            }
            reset={false}
            options={[
              {
                labelText: translate("divorce.pensionsExcluded.options.no.label", values),
                labelIcon: <CheckIcon />,
                labelIconActive: <CheckIcon active />,
                value: "no" /* no because NOT pensionsExcluded */,
              },
              {
                labelText: translate("divorce.pensionsExcluded.options.yes.label", values),
                labelIcon: <CrossIcon />,
                labelIconActive: <CrossIcon active />,
                value: "yes" /* yes because pensionsExcluded is TRUE */,
                optionHintText: "Achtung: Der Ausgleich ist gesetzlich vorgeschrieben. Ein Ausschluss ist nur per Notar möglich.",
              },
            ]}
            validators={[
              {
                validator: isRequired,
                message: "Bitte geben Sie an ob der Versorgungsausgleich durchgeführt wird",
              },
            ]}
          />
          <Grid container>
            {values.pensionsExcluded === "no" && (
              <>
                <Grid item xs={10}>
                  <ValidatorSelectField
                    label={translate("divorce.numberPensions.label", values)}
                    name="numberPensions"
                    validators={[
                      {
                        validator: isRequired,
                        message: "Bitte geben Sie die Anzahl Ihrer Rentenversicherungen an",
                      },
                    ]}
                  >
                    <MenuItem value={"0"}>{translate("divorce.numberPensions.options.0.label", values)}</MenuItem>
                    <MenuItem value={"1"}>{translate("divorce.numberPensions.options.1.label", values)}</MenuItem>
                    <MenuItem value={"2"}>{translate("divorce.numberPensions.options.2.label", values)}</MenuItem>
                    <MenuItem value={"3"}>{translate("divorce.numberPensions.options.3.label", values)}</MenuItem>
                    <MenuItem value={"4"}>{translate("divorce.numberPensions.options.4.label", values)}</MenuItem>
                    <MenuItem value={"5"}>{translate("divorce.numberPensions.options.5.label", values)}</MenuItem>
                  </ValidatorSelectField>
                </Grid>
                <Grid item xs={2}>
                  <Stack  justifyContent={"center"} alignItems={"center"} sx={{ height: "100%" }}>
                    <InfoIconButton onClick={() => setPensionsModalOpen("client")} />
                  </Stack>
                </Grid>
                <Grid item xs={10}>
                  <ValidatorSelectField
                    label={translate("divorce.numberPensionsPartner.label", values)}
                    name="numberPensionsPartner"
                    validators={[
                      {
                        validator: isRequired,
                        message: "Bitte geben Sie die Anzahl der Rentenversicherungen Ihres Partners an",
                      },
                    ]}
                    helperText="Sollten Sie hier unsicher sein, geben Sie hier zunächst einfach den Standard “1” an."
                  >
                    <MenuItem value={"0"}>{translate("divorce.numberPensionsPartner.options.0.label", values)}</MenuItem>
                    <MenuItem value={"1"}>{translate("divorce.numberPensionsPartner.options.1.label", values)}</MenuItem>
                    <MenuItem value={"2"}>{translate("divorce.numberPensionsPartner.options.2.label", values)}</MenuItem>
                    <MenuItem value={"3"}>{translate("divorce.numberPensionsPartner.options.3.label", values)}</MenuItem>
                    <MenuItem value={"4"}>{translate("divorce.numberPensionsPartner.options.4.label", values)}</MenuItem>
                    <MenuItem value={"5"}>{translate("divorce.numberPensionsPartner.options.5.label", values)}</MenuItem>
                  </ValidatorSelectField>
                </Grid>
                <Grid item xs={2}>
                  <Stack justifyContent={"center"} alignItems={"center"} sx={{ height: "70%"}}>
                    <InfoIconButton onClick={() => setPensionsModalOpen("partner")} />
                  </Stack>
                </Grid>
                <Grid item xs={12} sx={wizardStyle.formSpacer}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="pensionsQuestions"
                        name="pensionsQuestions"
                        checked={!!formValue(values, "pensionsQuestions")}
                        onChange={handleChange}
                        value="pensionsQuestions"
                        color="primary"
                      />
                    }
                    label={translate("divorce.pensionsQuestions.label", values)}
                  />
                </Grid>
                <PensionsDivorceModal personType={pensionsModalOpen} onClose={() => setPensionsModalOpen("")} open={!!pensionsModalOpen} />
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
